import React, { useState } from "react";
import { User, Lock, Palette, Bell, Globe } from "lucide-react";
import { motion } from "framer-motion";
import UserInformation from "../components/settings/UserInformation";
import ChangePassword from "../components/settings/ChangePassword";
import Appearance from "../components/settings/Appearance";
import NotificationsTab from "../components/settings/NotificationsTab";
import LanguageTab from "../components/settings/LanguageTab";
import { useTranslation } from "../i18n";

const SettingsPage = () => {
  const { t } = useTranslation("settings");
  const [activeTab, setActiveTab] = useState("profile");

  const tabs = [
    {
      id: "profile",
      label: t("tabs.profile"),
      icon: User,
      component: UserInformation
    },
    {
      id: "password",
      label: t("tabs.password"),
      icon: Lock,
      component: ChangePassword
    },
    {
      id: "appearance",
      label: t("tabs.appearance"),
      icon: Palette,
      component: Appearance
    },
    {
      id: "notifications",
      label: t("tabs.notifications"),
      icon: Bell,
      component: NotificationsTab
    },
    {
      id: "language",
      label: t("tabs.language"),
      icon: Globe,
      component: LanguageTab
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  return (
    <div className="min-h-screen bg-gray-50 pb-8 pt-3 sm:pt-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          {/* Tabs — 2x2 grid on mobile so every tab is visible without scrolling; horizontal on desktop */}
          <div className="border-b border-gray-200 p-2 sm:p-0">
            <nav
              className="grid grid-cols-2 gap-1 sm:flex sm:gap-8 sm:px-6"
              aria-label={t("tabs_nav_label")}
            >
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 py-2 sm:py-4 px-2 sm:px-1 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors
                      rounded-full sm:rounded-none border-2 sm:border-0 sm:border-b-2
                      ${isActive
                        ? "bg-purple-50 sm:bg-transparent border-purple-600 text-purple-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }
                    `}
                  >
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="p-3 sm:p-6"
          >
            {ActiveComponent && <ActiveComponent />}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
